/**
 * Checkout Service
 *
 * Handles PaymentIntent creation and embedded checkout flow management.
 * Communicates with the backend Checkout Lambda via API Gateway.
 * 
 * Per BSF v2.2.1 Phase 3.6 spec:
 * - Creates PaymentIntent server-side
 * - Returns clientSecret for Stripe Elements
 * - Supports embedded checkout (user stays on-site)
 */

export interface CartItem {
  id: string;
  name: string;
  description?: string;
  price: number; // in cents
  quantity: number;
  metadata?: Record<string, string>;
}

export interface Cart {
  items: CartItem[];
  customerId?: string;
  metadata?: Record<string, string>;
}

/**
 * Checkout session returned from backend
 * Per Phase 3.6 spec Task T10.1.6
 */
export interface CheckoutSession {
  clientSecret: string;
  paymentIntentId: string;
  orderId: string;
  amount: number;
  currency: string;
  /** Short-lived token that lets the confirmation page ask about this order */
  pollingToken?: string;
  discount?: {
    percentOff: number;
    originalAmount: number;
    discountedAmount: number;
  };
}

const pollingTokenKey = (orderId: string): string => `pollingToken_${orderId}`;

/**
 * Returns the polling token checkout issued for an order in this browser
 * session, or null. The confirmation page sends it as a Bearer token.
 */
export function getPollingToken(orderId: string): string | null {
  try {
    return sessionStorage.getItem(pollingTokenKey(orderId));
  } catch {
    return null;
  }
}

export interface CreateCheckoutRequest {
  cart: Cart;
  email?: string;
  returnUrl: string;
  tenantId?: string;
}

export interface CheckoutResult {
  success: boolean;
  paymentIntentId?: string;
  orderId?: string;
  error?: string;
}

/**
 * API Gateway endpoint for checkout service
 *
 * Production: https://7q640zvg8i.execute-api.us-east-1.amazonaws.com/prod
 * Dev: https://wurfnq091m.execute-api.us-east-1.amazonaws.com/dev
 */
const CHECKOUT_API_URL =
  import.meta.env.VITE_CHECKOUT_API_URL ||
  'https://7q640zvg8i.execute-api.us-east-1.amazonaws.com/prod';

const DEFAULT_TENANT_ID = 'TENANT#geauxfile';

export interface PurchaseLinkHandle { version: 1; id: string; capability: string }
export interface PurchaseLinkResult {
  purchaseLink: { version: 1; id: string; decision: 'resume' | 'receipt' | 'support'; reason: string };
  orderId: string;
  paymentIntentId?: string;
  clientSecret?: string;
  amount?: number;
  currency?: string;
  pollingToken?: string;
}
const purchaseIdPattern = /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/;
const capabilityPattern = /^[0-9a-f]{64}$/;
const capturedLinks = new Map<string, PurchaseLinkHandle>();
const pendingLinks = new Map<string, Promise<PurchaseLinkResult>>();

/** Enable only after the deployed Checkout API supports purchase-link v1. */
function purchaseLinkResumeEnabled(): boolean {
  return import.meta.env.VITE_PURCHASE_LINK_RESUME_ENABLED === 'true';
}

/** Historical product-only links cannot recreate a purchase. Current producers opt into new entry. */
export function isPurchaseLinkEntry(search: string, hash: string): boolean {
  const fragment = new URLSearchParams(hash.replace(/^#/, ''));
  // A bound or incomplete purchase always wins over a fresh-entry marker.
  if (['purchase', 'cap', 'support'].some(key => fragment.has(key))) return true;
  // Preserve the deployed product/cart contract until its replacement is verified.
  if (!purchaseLinkResumeEnabled()) return false;
  const query = new URLSearchParams(search);
  const freshEntry = query.getAll('entry').length === 1 && query.get('entry') === 'new';
  return query.has('offeringId') && !freshEntry;
}

/** Run before React/analytics mount. A capability never remains in page_location. */
export function capturePurchaseLink(): void {
  if (!['/guest-checkout', '/checkout'].includes(window.location.pathname)) return;
  const fragment = new URLSearchParams(window.location.hash.slice(1));
  if (!fragment.has('cap')) return;
  const id = fragment.get('purchase') ?? '';
  const capability = fragment.get('cap') ?? '';
  if (purchaseIdPattern.test(id) && capabilityPattern.test(capability) &&
    fragment.getAll('cap').length === 1 && fragment.getAll('purchase').length === 1) {
    const handle: PurchaseLinkHandle = { version: 1, id, capability };
    capturedLinks.set(id, handle);
    try { sessionStorage.setItem(`purchaseLink_v1_${id}`, JSON.stringify(handle)); } catch { /* Memory remains usable until refresh. */ }
  } else if (purchaseIdPattern.test(id)) {
    capturedLinks.delete(id);
    try { sessionStorage.removeItem(`purchaseLink_v1_${id}`); } catch { /* An invalid link remains unusable in memory. */ }
  }
  window.history.replaceState(window.history.state, '',
    `${window.location.pathname}${window.location.search}#${purchaseIdPattern.test(id) ? `purchase=${id}` : 'support'}`);
}

/**
 *
 */
export function readPurchaseLink(hash: string): PurchaseLinkHandle | null {
  const fragment = new URLSearchParams(hash.replace(/^#/, ''));
  const id = fragment.get('purchase') ?? '';
  if (!purchaseIdPattern.test(id) || fragment.getAll('purchase').length !== 1) return null;
  if (fragment.has('cap')) {
    const capability = fragment.get('cap') ?? '';
    return fragment.getAll('cap').length === 1 && capabilityPattern.test(capability)
      ? { version: 1, id, capability } : null;
  }
  const memory = capturedLinks.get(id);
  if (memory) return memory;
  try {
    const raw: unknown = JSON.parse(sessionStorage.getItem(`purchaseLink_v1_${id}`) ?? 'null');
    if (raw && typeof raw === 'object' && 'version' in raw && raw.version === 1 &&
      'id' in raw && raw.id === id && 'capability' in raw && typeof raw.capability === 'string' && capabilityPattern.test(raw.capability)) {
      return { version: 1, id, capability: raw.capability };
    }
  } catch { /* Missing or denied browser storage is a status/support path. */ }
  return null;
}

/** Coalesce refreshes, retain the original handle, and never call the legacy creator. */
export function resumePurchaseLink(handle: PurchaseLinkHandle): Promise<PurchaseLinkResult> {
  if (handle.version !== 1 || !purchaseIdPattern.test(handle.id) || !capabilityPattern.test(handle.capability)) {
    return Promise.reject(new CheckoutError('This purchase link is incomplete.', 'INVALID_PURCHASE_LINK'));
  }
  if (!purchaseLinkResumeEnabled()) {
    return Promise.reject(new CheckoutError(
      'Please contact Geaux File to verify this purchase.', 'PURCHASE_LINK_NOT_ENABLED'
    ));
  }
  const key = `${handle.id}:${handle.capability}`;
  const pending = pendingLinks.get(key);
  if (pending) return pending;
  const request = (async (): Promise<PurchaseLinkResult> => {
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 15000);
    try {
      const response = await fetch(`${CHECKOUT_API_URL}/checkout/session`, {
        method: 'POST', headers: { 'Content-Type': 'application/json', 'X-Tenant-Id': DEFAULT_TENANT_ID },
        body: JSON.stringify({ purchaseLink: handle }), signal: controller.signal, cache: 'no-store',
      });
      if (!response.ok) throw new CheckoutError('We could not verify this purchase. Contact Geaux File for help.', 'PURCHASE_LINK_UNAVAILABLE');
      const raw: unknown = await response.json();
      if (!raw || typeof raw !== 'object' || Array.isArray(raw)) throw new Error('Invalid purchase response');
      const data = raw as Record<string, unknown>;
      const rawLink = data['purchaseLink'];
      if (!rawLink || typeof rawLink !== 'object' || Array.isArray(rawLink)) throw new Error('Invalid purchase response');
      const link = rawLink as Record<string, unknown>;
      const decision = link['decision'];
      if (link['version'] !== 1 || link['id'] !== handle.id ||
        !['resume', 'receipt', 'support'].includes(String(decision)) ||
        typeof link['reason'] !== 'string' || !/^[a-z0-9_]{1,100}$/.test(link['reason']) ||
        data['orderId'] !== `ORD-${handle.id}` ||
        (decision !== 'resume' && data['clientSecret'] !== undefined)) throw new Error('Invalid purchase response');
      if (decision === 'resume' && (typeof data['clientSecret'] !== 'string' || !data['clientSecret'] ||
        typeof data['paymentIntentId'] !== 'string' || !/^pi_[A-Za-z0-9_]+$/.test(data['paymentIntentId']) ||
        typeof data['amount'] !== 'number' || !Number.isSafeInteger(data['amount']) || data['amount'] <= 0 ||
        data['currency'] !== 'usd')) throw new Error('Invalid purchase response');
      const result: PurchaseLinkResult = {
        purchaseLink: { version: 1, id: handle.id, decision: decision as PurchaseLinkResult['purchaseLink']['decision'], reason: link['reason'] },
        orderId: `ORD-${handle.id}`,
        ...(typeof data['paymentIntentId'] === 'string' && { paymentIntentId: data['paymentIntentId'] }),
        ...(decision === 'resume' && { clientSecret: data['clientSecret'] as string, amount: data['amount'] as number, currency: 'usd' }),
        ...(typeof data['pollingToken'] === 'string' && { pollingToken: data['pollingToken'] }),
      };
      if (result.pollingToken) {
        try { sessionStorage.setItem(pollingTokenKey(result.orderId), result.pollingToken); } catch { /* Receipt email remains available. */ }
      }
      return result;
    } finally { clearTimeout(timeout); }
  })().finally(() => { pendingLinks.delete(key); });
  pendingLinks.set(key, request);
  return request;
}

/**
 * Creates a checkout session with PaymentIntent
 * Returns clientSecret for Stripe Elements
 * 
 * Per Phase 3.6 spec: POST /checkout/create-session
 */
export async function createCheckoutSession(
  request: CreateCheckoutRequest
): Promise<CheckoutSession> {
  const response = await fetch(`${CHECKOUT_API_URL}/checkout/session`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-Tenant-Id': request.tenantId || DEFAULT_TENANT_ID,
    },
    body: JSON.stringify({
      items: request.cart.items.map((item) => ({
        priceId: item.id,
        productName: item.name,
        amount: item.price,
        quantity: item.quantity,
        metadata: { ...request.cart.metadata, ...item.metadata },
      })),
      customerId: request.cart.customerId,
      customerEmail: request.email,
      metadata: {
        ...request.cart.metadata,
        returnUrl: request.returnUrl,
      },
    }),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new CheckoutError(
      errorData.message || 'Failed to create checkout session',
      errorData.error || errorData.code || 'CHECKOUT_ERROR'
    );
  }

  const data = await response.json();
  
  // Validate response shape per Phase 3.6 spec
  if (!data.clientSecret) {
    throw new CheckoutError(
      'Invalid response: missing clientSecret',
      'INVALID_RESPONSE'
    );
  }

  // Keep the polling token for the confirmation page after the Stripe redirect
  if (data.pollingToken && data.orderId) {
    try {
      sessionStorage.setItem(pollingTokenKey(data.orderId), data.pollingToken);
    } catch {
      // Storage unavailable: the page falls back to email recovery
    }
  }

  return {
    clientSecret: data.clientSecret,
    paymentIntentId: data.paymentIntentId,
    orderId: data.orderId,
    amount: data.amount,
    currency: data.currency,
    ...(data.pollingToken && { pollingToken: data.pollingToken }),
    ...(data.discount && { discount: data.discount }),
  };
}

/**
 * Retrieves the status of an existing PaymentIntent
 */
export async function getCheckoutStatus(paymentIntentId: string): Promise<{
  status: string;
  amount: number;
  currency: string;
}> {
  const response = await fetch(`${CHECKOUT_API_URL}/checkout/status/${paymentIntentId}`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      'X-Tenant-Id': DEFAULT_TENANT_ID,
    },
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new CheckoutError(
      errorData.message || 'Failed to get checkout status',
      errorData.code || 'STATUS_ERROR'
    );
  }

  return response.json();
}

/**
 * Calculates cart totals
 */
export function calculateCartTotals(cart: Cart): {
  subtotal: number;
  tax: number;
  total: number;
} {
  const subtotal = cart.items.reduce((sum, item) => sum + item.price * item.quantity, 0);
  // Tax calculation would typically come from backend based on location
  const taxRate = 0; // Registered agent services generally not taxable
  const tax = Math.round(subtotal * taxRate);
  const total = subtotal + tax;

  return { subtotal, tax, total };
}

/**
 * Formats amount in cents to display string
 */
export function formatAmount(cents: number, currency = 'USD'): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
  }).format(cents / 100);
}

/**
 * Custom error class for checkout-related errors
 */
/**
 * Updates PaymentIntent metadata after wallet payment provides billing details.
 * Used by ExpressCheckoutElement (Apple Pay, Google Pay, PayPal) to attach
 * guestEmail/name that the downstream pipeline needs for order creation,
 * magic link, and notifications.
 */
export async function updateCheckoutMetadata(
  paymentIntentId: string,
  metadata: { guestEmail: string; guestFirstName: string; guestLastName: string; changeRA?: string; termsConsentAt?: string }
): Promise<void> {
  const response = await fetch(`${CHECKOUT_API_URL}/checkout/update-metadata`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-Tenant-Id': DEFAULT_TENANT_ID,
    },
    body: JSON.stringify({ paymentIntentId, metadata }),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new CheckoutError(
      errorData.message || 'Failed to update payment metadata',
      errorData.code || 'METADATA_UPDATE_ERROR'
    );
  }
}

/**
 * Check if an email has an existing customer portal account.
 * Used by guest checkout to surface a "sign in" nudge.
 * Fail-silent: returns false on any error (never blocks checkout).
 */
export async function checkEmailAccount(email: string): Promise<{ accountDetected: boolean }> {
  try {
    const response = await fetch(`${CHECKOUT_API_URL}/checkout/check-email`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email: email.trim().toLowerCase() }),
    });
    if (!response.ok) return { accountDetected: false };
    return response.json();
  } catch {
    return { accountDetected: false };
  }
}

// =============================================================================
// PayPal
// =============================================================================

export interface PayPalOrderResult {
  paypalOrderId: string;
  orderId: string;
  amount: number;
}

export interface PayPalCaptureResult {
  success: boolean;
  orderId: string;
  paypalOrderId: string;
  status: string;
}

/**
 * Creates a PayPal order via backend (server-side price validation + PayPal API).
 * Returns the PayPal order ID for the JS SDK popup and our internal orderId.
 */
export async function createPayPalOrder(
  request: CreateCheckoutRequest
): Promise<PayPalOrderResult> {
  const response = await fetch(`${CHECKOUT_API_URL}/checkout/paypal/create-order`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-Tenant-Id': request.tenantId || DEFAULT_TENANT_ID,
    },
    body: JSON.stringify({
      items: request.cart.items.map((item) => ({
        priceId: item.id,
        productName: item.name,
        amount: item.price,
        quantity: item.quantity,
        metadata: { ...request.cart.metadata, ...item.metadata },
      })),
      customerId: request.cart.customerId,
      customerEmail: request.email,
      metadata: request.cart.metadata,
    }),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new CheckoutError(
      errorData.message || 'Failed to create PayPal order',
      errorData.error || 'PAYPAL_CREATE_ERROR'
    );
  }

  return response.json();
}

/**
 * Captures a PayPal order after user approval.
 * Backend captures the payment and emits Payment.Succeeded to EventBridge.
 */
export async function capturePayPalOrder(
  paypalOrderId: string,
  orderId: string,
  metadata: Record<string, string>
): Promise<PayPalCaptureResult> {
  const response = await fetch(`${CHECKOUT_API_URL}/checkout/paypal/capture-order`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-Tenant-Id': DEFAULT_TENANT_ID,
    },
    body: JSON.stringify({ paypalOrderId, orderId, metadata }),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new CheckoutError(
      errorData.message || 'Failed to capture PayPal payment',
      errorData.error || 'PAYPAL_CAPTURE_ERROR'
    );
  }

  return response.json();
}

/**
 *
 */
export class CheckoutError extends Error {
  public code: string;

  constructor(message: string, code: string) {
    super(message);
    this.name = 'CheckoutError';
    this.code = code;
  }

  /**
   * Check if the error is retryable
   */
  public isRetryable(): boolean {
    return ['NETWORK_ERROR', 'TIMEOUT', 'RATE_LIMIT'].includes(this.code);
  }
}
